"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, RefObject } from "react";
import Link from "next/link";
import styles from "./work.module.css";
import PostWork from "./PostWork";

type SiftScreen = {
  src: string;
  width: number;
  height: number;
  stage: string;
  label: string;
  headline: string;
  note: string;
};

type CgScreen = Omit<SiftScreen, "width" | "height">;

function workImageSrcSet(src: string) {
  return `${src.replace(/\.webp$/, "-400.webp")} 400w, ${src.replace(/\.webp$/, "-720.webp")} 720w, ${src.replace(/\.webp$/, "-960.webp")} 960w, ${src} 1440w`;
}

function siftPreviewSrc(src: string) {
  return src.replace(/\.webp$/, "-180.webp");
}

const screens: SiftScreen[] = [
  { src: "/work/sift/01-overview.webp", width: 1440, height: 794, stage: "Orient", label: "Search overview", headline: "See the whole search without losing the thread.", note: "Interviews, offers, match quality and pipeline movement resolve into one controlled starting point." },
  { src: "/work/sift/02-match-intelligence.webp", width: 1440, height: 795, stage: "Qualify", label: "Match intelligence", headline: "Know why an opportunity deserves your attention.", note: "Evidence behind every fit score makes the recommendation legible before time is invested." },
  { src: "/work/sift/03-pipeline.webp", width: 1440, height: 793, stage: "Progress", label: "Pipeline board", headline: "See the journey and choose the next move.", note: "Every application stays visible from first match through interview, so action never depends on memory." },
  { src: "/work/sift/04-top-matches.webp", width: 1440, height: 797, stage: "Prioritize", label: "Top matches", headline: "Put the strongest opportunities first.", note: "Ranked matches turn a crowded market into a deliberate, evidence-led shortlist." },
  { src: "/work/sift/05-applied.webp", width: 1440, height: 794, stage: "Track", label: "Applied roles", headline: "Keep momentum visible after the application.", note: "Status, timing and next actions remain attached to every role after apply." },
  { src: "/work/sift/06-tailored-resume.webp", width: 1440, height: 797, stage: "Tailor", label: "Resume tailoring", headline: "Turn role evidence into stronger application material.", note: "Resume analysis shapes experience to the opportunity without losing the candidate's real voice." },
  { src: "/work/sift/08-conversations.webp", width: 1440, height: 795, stage: "Connect", label: "Outreach", headline: "Build a relationship around the opportunity.", note: "Conversations and follow-up live beside role context, creating continuity without spray-and-pray automation." },
];

const cgScreens: CgScreen[] = [
  { src: "/work/cg/01-hero.webp", stage: "Welcome", label: "Homepage hero", headline: "Turn renovation uncertainty into a clear first step.", note: "A direct promise, visible service scope and a fast quote path give GTA homeowners confidence before the first conversation." },
  { src: "/work/cg/02-gallery.webp", stage: "Explore", label: "Project gallery", headline: "Let completed spaces prove the standard of the work.", note: "Real kitchens, basements and full-scope interiors make capability tangible without relying on generic construction claims." },
  { src: "/work/cg/03-gallery-detail.webp", stage: "Inspect", label: "Gallery detail", headline: "Show the transformation, not just the finished surface.", note: "Before, in-progress and completed photography reveals the workmanship behind each renovation and keeps every claim grounded." },
  { src: "/work/cg/04-testimonials.webp", stage: "Trust", label: "Client testimonials", headline: "Finish the journey with evidence from real clients.", note: "Detailed reviews and a visible 4.8 rating turn project interest into the confidence needed to request a quote." },
];

const cadenceScreens: SiftScreen[] = [
  { src: "/work/cadencestack/01-pipeline.webp", stage: "Analyze", label: "Content pipeline", headline: "Read the post before it's posted.", note: "Every draft is scored for voice and strength before it reaches your queue." },
  { src: "/work/cadencestack/02-analytics.webp", stage: "Score", label: "Performance analytics", headline: "Numbers your growth can't hide from.", note: "Impressions, engagement, and DMs tracked against every post you publish." },
  { src: "/work/cadencestack/03-pillars.webp", stage: "Recommend", label: "Content pillars", headline: "A plan, not a guess.", note: "Content pillars built from what's actually working, not what should." },
];

const chapterImageCache = new Map<string, Promise<void>>();

function warmChapterImage(src: string) {
  const cached = chapterImageCache.get(src);
  if (cached) return cached;
  const promise = new Promise<void>((resolve) => {
    const image = new Image();
    image.decoding = "async";
    image.src = src;
    image.decode().catch(() => undefined).finally(resolve);
  });
  chapterImageCache.set(src, promise);
  return promise;
}

const capitalScreens = [
  { src: "/work/capital-command/01-overview.webp", label: "Overview", headline: "Every market, one desk.", note: "Cross-market signals surfaced before the noise catches up." },
  { src: "/work/capital-command/02-portfolio.webp", label: "Portfolio", headline: "Positions, not guesses.", note: "Portfolio tracking built for how research actually happens." },
  { src: "/work/capital-command/03-markets.webp", label: "Markets", headline: "Read the tape, not the headlines.", note: "Cross-market data pulled into one continuously refreshed view." },
  { src: "/work/capital-command/04-ipo-intelligence.webp", label: "IPO Intelligence", headline: "18 IPOs. Two markets. Zero blind spots.", note: "10 US listings and 8 Indian listings tracked side by side." },
  { src: "/work/capital-command/05-signal-desk.webp", label: "Signal Desk", headline: "A number for how much to trust it.", note: "Every signal carries a 92-point confidence score, not a hunch." },
  { src: "/work/capital-command/06-research-lab.webp", label: "Research Lab", headline: "Where the thesis gets tested.", note: "Research built the way an analyst actually works through a call." },
  { src: "/work/capital-command/07-risk-center.webp", label: "Risk Center", headline: "Know what you're exposed to.", note: "Risk surfaced before it becomes the headline." },
] as const;

type FramePose = {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotateY: number;
  opacity: number;
  prominence: number;
};

type PerfSnapshot = {
  fps: number;
  rawFps: number;
  p95FrameInterval: number;
  maxFrameInterval: number;
  suspensionGaps: number;
  longTasksOver50ms: number;
  longestTask: number;
  cls: number;
  decodedBeforeFocus: boolean;
  decodeMisses: number[];
  touchAction: string;
  scrollListenerPassive: boolean;
  minSurfaceGap: number | null;
  collisions: number;
  samples: number;
  minTextGap?: number | null;
  textCollisions?: number;
  firstRenderedTextGap?: number | null;
  minPanelViewportInset?: number | null;
  clippedPanelSamples?: number;
  focusedPanelClippedSamples?: number;
};

// Detailed animation telemetry is retained for intentional QA sessions, but it
// should not sample frames or observe layout continuously for regular visitors.
const performanceAuditEnabled = () =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("averr-perf");

declare global {
  interface Window {
    __AVERR_SIFT_PERF__?: PerfSnapshot;
    __AVERR_CG_PERF__?: PerfSnapshot;
    __AVERR_CADENCE_PERF__?: PerfSnapshot;
    __AVERR_CAPITAL_PERF__?: { fps: number; p95FrameInterval: number; longTasksOver50ms: number; longestTask: number; cls: number; samples: number };
  }
}

const PRIMARY_WIDTH = 70;
const SIDE_X = 70;
const SCREEN_TRANSITIONS = screens.length - 1;
const MAX_STAGES_PER_SECOND = .82;
const AUTO_STAGE_SECONDS = 5.25;

function ease(value: number) {
  const t = Math.min(1, Math.max(0, value));
  return t * t * (3 - 2 * t);
}

function mix(from: number, to: number, amount: number) {
  return from + (to - from) * amount;
}

function getPose(index: number, segment: number, local: number): FramePose {
  const primary: FramePose = { x: 0, y: 0, z: 92, scale: 1, rotateY: 0, opacity: 1, prominence: 1 };
  const previous: FramePose = { x: -SIDE_X, y: 2.2, z: -245, scale: .46, rotateY: 12, opacity: .58, prominence: .25 };
  const next: FramePose = { x: SIDE_X, y: -1.8, z: -270, scale: .46, rotateY: -12, opacity: .56, prominence: .22 };
  const farPrevious: FramePose = { x: -105, y: 4.5, z: -520, scale: .31, rotateY: 17, opacity: 0, prominence: 0 };
  const farNext: FramePose = { x: 105, y: -3.8, z: -560, scale: .31, rotateY: -17, opacity: 0, prominence: 0 };

  const interpolate = (a: FramePose, b: FramePose, t: number): FramePose => ({
    x: mix(a.x, b.x, t), y: mix(a.y, b.y, t), z: mix(a.z, b.z, t),
    scale: mix(a.scale, b.scale, t), rotateY: mix(a.rotateY, b.rotateY, t),
    opacity: mix(a.opacity, b.opacity, t), prominence: mix(a.prominence, b.prominence, t),
  });

  if (index < segment - 1) return farPrevious;
  if (index > segment + 1) return farNext;
  if (index === segment - 1) {
    if (local < .62) return previous;
    return interpolate(previous, farPrevious, ease((local - .62) / .14));
  }
  if (index === segment) {
    // Hold for ~4.4s of each 5.25s automatic stage. The outgoing surface fully
    // recedes before the incoming one begins to enlarge.
    if (local < .84) return primary;
    return interpolate(primary, previous, ease((local - .84) / .08));
  }
  if (index === segment + 1) {
    if (local < .92) return next;
    return interpolate(next, primary, ease((local - .92) / .08));
  }
  return farNext;
}

function useCorridorProgress(enabled: boolean) {
  const sectionRef = useRef<HTMLElement>(null);
  const jumpRequestRef = useRef<number | null>(null);
  const jumpLockRef = useRef<{ value: number; until: number } | null>(null);
  const [progress, setProgress] = useState(0);
  const [isLooping, setIsLooping] = useState(false);

  const jumpToStage = useCallback((index: number) => {
    const normalized = Math.min(1, Math.max(0, index / SCREEN_TRANSITIONS));
    jumpRequestRef.current = normalized;
    jumpLockRef.current = { value: normalized, until: performance.now() + 9000 };
    setProgress(normalized);
    setIsLooping(false);

    const section = sectionRef.current;
    if (!section) return;
    const rect = section.getBoundingClientRect();
    const travel = Math.max(1, rect.height - window.innerHeight);
    const sectionTop = window.scrollY + rect.top;
    window.scrollTo({ top: sectionTop + normalized * travel, behavior: "auto" });
  }, []);

  useEffect(() => {
    let raf = 0;
    let target = 0;
    let visual = 0;
    let previousTime = performance.now();
    let initialized = false;
    let manualUntil = 0;
    let loopStarted = 0;

    const readTarget = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      target = Math.min(1, Math.max(0, -rect.top / travel));
      if (!initialized) {
        initialized = true;
        visual = target;
        setProgress(target);
      }
    };

    const markManual = () => {
      jumpLockRef.current = null;
      manualUntil = performance.now() + 9000;
      setIsLooping(false);
    };

    const animate = (now: number) => {
      const elapsed = Math.min(.05, Math.max(0, (now - previousTime) / 1000));
      previousTime = now;
      const section = sectionRef.current;
      const rect = section?.getBoundingClientRect();
      const corridorVisible = Boolean(rect && rect.top <= 2 && rect.bottom >= window.innerHeight - 2);

      if (jumpRequestRef.current !== null) {
        target = jumpRequestRef.current;
        visual = jumpRequestRef.current;
        jumpRequestRef.current = null;
        manualUntil = jumpLockRef.current?.until ?? now + 9000;
        loopStarted = 0;
        setIsLooping(false);
        setProgress(visual);
        raf = requestAnimationFrame(animate);
        return;
      }

      if (jumpLockRef.current && now < jumpLockRef.current.until) {
        target = jumpLockRef.current.value;
        visual = jumpLockRef.current.value;
        setProgress(visual);
        raf = requestAnimationFrame(animate);
        return;
      }
      if (jumpLockRef.current) jumpLockRef.current = null;

      if (enabled && now >= manualUntil && corridorVisible) {
        if (visual >= 1) {
          if (!loopStarted) {
            loopStarted = now;
            setIsLooping(true);
          }
          if (now - loopStarted > 1100) {
            visual = 0;
            target = 0;
            loopStarted = 0;
            setIsLooping(false);
          }
        } else {
          visual = Math.min(1, visual + elapsed / (AUTO_STAGE_SECONDS * SCREEN_TRANSITIONS));
        }
      } else {
        const difference = target - visual;
        const maxProgressStep = (MAX_STAGES_PER_SECOND / SCREEN_TRANSITIONS) * elapsed;
        if (Math.abs(difference) <= maxProgressStep) visual = target;
        else visual += Math.sign(difference) * maxProgressStep;
      }
      setProgress(visual);
      raf = requestAnimationFrame(animate);
    };

    const onScroll = () => readTarget();
    readTarget();
    raf = requestAnimationFrame(animate);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    window.addEventListener("wheel", markManual, { passive: true });
    window.addEventListener("touchstart", markManual, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("wheel", markManual);
      window.removeEventListener("touchstart", markManual);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled]);

  return { sectionRef, progress, isLooping, jumpToStage };
}

function usePerformanceAudit(activeIndex: number, decoded: boolean[], presented: boolean) {
  const decodedRef = useRef(decoded);
  const activeRef = useRef(activeIndex);
  const presentedRef = useRef(presented);
  const misses = useRef(new Set<number>());
  useEffect(() => {
    if (!performanceAuditEnabled()) return;
    decodedRef.current = decoded;
    activeRef.current = activeIndex;
    presentedRef.current = presented;
  }, [activeIndex, decoded, presented]);

  useEffect(() => {
    const intervals: number[] = [];
    const longTasks: number[] = [];
    let cls = 0;
    let previous = performance.now();
    let wasPresented = false;
    let frame = 0;
    let collisions = 0;
    let samples = 0;
    let minGap = Number.POSITIVE_INFINITY;

    const updateSnapshot = () => {
      // The verification browser may suspend background tabs between inspection
      // calls. Exclude those >=250ms suspension gaps from interactive frame
      // pacing, while retaining raw FPS and the gap count for transparency.
      const interactiveIntervals = intervals.filter((value) => value < 250);
      const sorted = [...interactiveIntervals].sort((a, b) => a - b);
      const elapsed = interactiveIntervals.reduce((sum, value) => sum + value, 0);
      const rawElapsed = intervals.reduce((sum, value) => sum + value, 0);
      const visible = [...document.querySelectorAll<HTMLElement>("[data-sift-frame]")]
        .filter((node) => Number(node.dataset.prominence || 0) > .08)
        .map((node) => node.getBoundingClientRect())
        .sort((a, b) => a.left - b.left);
      if (visible.length > 1) {
        samples += 1;
        for (let index = 1; index < visible.length; index += 1) {
          const gap = visible[index].left - visible[index - 1].right;
          minGap = Math.min(minGap, gap);
          if (gap < -1) collisions += 1;
        }
      }
      window.__AVERR_SIFT_PERF__ = {
        fps: elapsed ? (interactiveIntervals.length * 1000) / elapsed : 0,
        rawFps: rawElapsed ? (intervals.length * 1000) / rawElapsed : 0,
        p95FrameInterval: sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * .95))] : 0,
        maxFrameInterval: sorted.at(-1) || 0,
        suspensionGaps: intervals.length - interactiveIntervals.length,
        longTasksOver50ms: longTasks.length,
        longestTask: Math.max(0, ...longTasks),
        cls,
        decodedBeforeFocus: misses.current.size === 0,
        decodeMisses: [...misses.current],
        touchAction: getComputedStyle(document.querySelector("main") || document.body).touchAction,
        scrollListenerPassive: true,
        minSurfaceGap: Number.isFinite(minGap) ? minGap : null,
        collisions,
        samples,
      };
      const output = document.getElementById("sift-perf-metrics");
      if (output) output.dataset.snapshot = JSON.stringify(window.__AVERR_SIFT_PERF__);
    };

    const tick = (now: number) => {
      if (presentedRef.current) {
        if (wasPresented) intervals.push(now - previous);
        if (intervals.length > 1800) intervals.shift();
        wasPresented = true;
      } else {
        intervals.length = 0;
        wasPresented = false;
      }
      previous = now;
      if (presentedRef.current && !decodedRef.current[activeRef.current]) misses.current.add(activeRef.current + 1);
      if (frame % 12 === 0) updateSnapshot();
      frame += 1;
      raf = requestAnimationFrame(tick);
    };

    let raf = requestAnimationFrame(tick);
    const observers: PerformanceObserver[] = [];
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        if (!presentedRef.current) return;
        list.getEntries().forEach((entry) => { if (entry.duration > 50) longTasks.push(entry.duration); });
      });
      longTaskObserver.observe({ type: "longtask" });
      observers.push(longTaskObserver);
    } catch { /* Long Tasks API is not available in every browser. */ }
    try {
      const layoutObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
          if (!shift.hadRecentInput) cls += shift.value || 0;
        });
      });
      layoutObserver.observe({ type: "layout-shift", buffered: true });
      observers.push(layoutObserver);
    } catch { /* Layout Instability API is not available in every browser. */ }

    return () => {
      cancelAnimationFrame(raf);
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);
}

const CG_HOLD_MS = 4500;
const CG_HALF_TRANSITION_MS = 380;

function useCgSequence(enabled: boolean) {
  const [active, setActive] = useState(0);
  const [target, setTarget] = useState(0);
  const [phase, setPhase] = useState<"idle" | "release" | "entry">("idle");
  const [manualUntil, setManualUntil] = useState(0);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
  }, []);

  const goTo = useCallback((index: number, manual = true) => {
    if (index === active && phase === "idle") {
      if (manual) setManualUntil(performance.now() + 9000);
      return;
    }
    clearTimers();
    setTarget(index);
    setPhase("release");
    if (manual) setManualUntil(performance.now() + 9000);
    timers.current.push(window.setTimeout(() => {
      setActive(index);
      setPhase("entry");
      timers.current.push(window.setTimeout(() => setPhase("idle"), CG_HALF_TRANSITION_MS));
    }, CG_HALF_TRANSITION_MS));
  }, [active, clearTimers, phase]);

  useEffect(() => {
    if (!enabled || phase !== "idle") return;
    const wait = Math.max(CG_HOLD_MS, manualUntil - performance.now());
    const timer = window.setTimeout(() => goTo((active + 1) % cgScreens.length, false), wait);
    return () => window.clearTimeout(timer);
  }, [active, enabled, goTo, manualUntil, phase]);

  useEffect(() => clearTimers, [clearTimers]);
  return { active, target, phase, goTo };
}

function useCgPerformanceAudit(active: number, decoded: boolean[], enabled: boolean) {
  const activeRef = useRef(active);
  const decodedRef = useRef(decoded);
  const enabledRef = useRef(enabled);
  useEffect(() => {
    if (!performanceAuditEnabled()) return;
    activeRef.current = active;
    decodedRef.current = decoded;
    enabledRef.current = enabled;
  }, [active, decoded, enabled]);

  useEffect(() => {
    const intervals: number[] = [];
    const longTasks: number[] = [];
    const misses = new Set<number>();
    let cls = 0;
    let previous = performance.now();
    let frame = 0;
    let samples = 0;
    let collisions = 0;
    let minGap = Number.POSITIVE_INFINITY;
    let textCollisions = 0;
    let minTextGap = Number.POSITIVE_INFINITY;
    let firstRenderedTextGap: number | null = null;
    let minPanelViewportInset = Number.POSITIVE_INFINITY;
    let clippedPanelSamples = 0;
    let focusedPanelClippedSamples = 0;

    const samplePanels = () => {
      const panelNodes = [...document.querySelectorAll<HTMLElement>("[data-cg-panel]")];
      const rects = panelNodes
        .map((node) => node.getBoundingClientRect())
        .filter((rect) => rect.width > 2 && rect.height > 2);
      const sceneRect = document.querySelector<HTMLElement>(`.${styles.cgScene}`)?.getBoundingClientRect();
      panelNodes.forEach((node) => {
        const board = node.querySelector<HTMLElement>(`.${styles.cgBoard}`);
        if (!board || !sceneRect) return;
        const rect = board.getBoundingClientRect();
        const inset = Math.min(
          rect.left - Math.max(0, sceneRect.left),
          rect.top - Math.max(0, sceneRect.top),
          Math.min(window.innerWidth, sceneRect.right) - rect.right,
          Math.min(window.innerHeight, sceneRect.bottom) - rect.bottom,
        );
        minPanelViewportInset = Math.min(minPanelViewportInset, inset);
        if (inset < 0) clippedPanelSamples += 1;
        if (node.classList.contains(styles.cgFocusPanel) && inset < 0) focusedPanelClippedSamples += 1;
      });
      for (let a = 0; a < rects.length; a += 1) {
        for (let b = a + 1; b < rects.length; b += 1) {
          const dx = Math.max(rects[a].left - rects[b].right, rects[b].left - rects[a].right, 0);
          const dy = Math.max(rects[a].top - rects[b].bottom, rects[b].top - rects[a].bottom, 0);
          const gap = Math.hypot(dx, dy);
          minGap = Math.min(minGap, gap);
          if (gap < 48) collisions += 1;
        }
      }
      const textNodes = [...document.querySelectorAll<HTMLElement>("[data-cg-text]")]
        .map((node) => node.getBoundingClientRect())
        .filter((rect) => rect.width > 2 && rect.height > 2);
      if (textNodes.length) {
        const textRect = {
          left: Math.min(...textNodes.map((rect) => rect.left)),
          right: Math.max(...textNodes.map((rect) => rect.right)),
          top: Math.min(...textNodes.map((rect) => rect.top)),
          bottom: Math.max(...textNodes.map((rect) => rect.bottom)),
        };
        let frameTextGap = Number.POSITIVE_INFINITY;
        rects.forEach((rect) => {
          const dx = Math.max(rect.left - textRect.right, textRect.left - rect.right, 0);
          const dy = Math.max(rect.top - textRect.bottom, textRect.top - rect.bottom, 0);
          frameTextGap = Math.min(frameTextGap, Math.hypot(dx, dy));
        });
        minTextGap = Math.min(minTextGap, frameTextGap);
        if (firstRenderedTextGap === null) firstRenderedTextGap = frameTextGap;
        if (frameTextGap < (window.innerWidth <= 720 ? 20 : 48)) textCollisions += 1;
      }
      samples += 1;
    };

    const update = () => {
      const interactive = intervals.filter((value) => value < 250);
      const sorted = [...interactive].sort((a, b) => a - b);
      const elapsed = interactive.reduce((sum, value) => sum + value, 0);
      const rawElapsed = intervals.reduce((sum, value) => sum + value, 0);
      window.__AVERR_CG_PERF__ = {
        fps: elapsed ? interactive.length * 1000 / elapsed : 0,
        rawFps: rawElapsed ? intervals.length * 1000 / rawElapsed : 0,
        p95FrameInterval: sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * .95))] || 0,
        maxFrameInterval: sorted.at(-1) || 0,
        suspensionGaps: intervals.length - interactive.length,
        longTasksOver50ms: longTasks.length,
        longestTask: Math.max(0, ...longTasks),
        cls,
        decodedBeforeFocus: misses.size === 0,
        decodeMisses: [...misses],
        touchAction: getComputedStyle(document.querySelector("main") || document.body).touchAction,
        scrollListenerPassive: true,
        minSurfaceGap: Number.isFinite(minGap) ? minGap : null,
        collisions,
        samples,
        minTextGap: Number.isFinite(minTextGap) ? minTextGap : null,
        textCollisions,
        firstRenderedTextGap,
        minPanelViewportInset: Number.isFinite(minPanelViewportInset) ? minPanelViewportInset : null,
        clippedPanelSamples,
        focusedPanelClippedSamples,
      };
      const output = document.getElementById("cg-perf-metrics");
      if (output) output.dataset.snapshot = JSON.stringify(window.__AVERR_CG_PERF__);
    };

    const tick = (now: number) => {
      if (enabledRef.current) {
        intervals.push(now - previous);
        if (!decodedRef.current[activeRef.current]) misses.add(activeRef.current + 1);
        if (frame % 12 === 0) {
          samplePanels();
          update();
        }
      }
      previous = now;
      frame += 1;
      raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    const observers: PerformanceObserver[] = [];
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
        if (enabledRef.current && entry.duration > 50) longTasks.push(entry.duration);
      }));
      observer.observe({ type: "longtask" });
      observers.push(observer);
    } catch { /* Long Tasks API is optional. */ }
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
        const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!shift.hadRecentInput) cls += shift.value || 0;
      }));
      observer.observe({ type: "layout-shift", buffered: true });
      observers.push(observer);
    } catch { /* Layout Instability API is optional. */ }
    return () => {
      cancelAnimationFrame(raf);
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);
}

function CgChapter() {
  const chapterRef = useRef<HTMLElement>(null);
  const [decoded, setDecoded] = useState(() => cgScreens.map(() => false));
  const [intro, setIntro] = useState(true);
  const [opening, setOpening] = useState(false);
  const [visible, setVisible] = useState(false);
  // The primary capture is the only rendering gate. The remaining lossless
  // captures continue decoding before their timed focus, so entering the
  // chapter never waits on four full-size PNG decodes.
  const ready = decoded[0];
  const { active, target, phase, goTo } = useCgSequence(ready && visible && !intro);
  useCgPerformanceAudit(active, decoded, ready && visible);

  useEffect(() => {
    const chapter = chapterRef.current;
    if (!chapter) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= .18), { threshold: [.18] });
    observer.observe(chapter);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    // CadenceStack is the next chapter. Decode its real captures while the
    // visitor is still inside CG so Cadence never begins with decode work.
    cadenceScreens.forEach((screen) => { void warmChapterImage(screen.src); });
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    cgScreens.forEach((screen, index) => {
      const image = new Image();
      image.src = screen.src;
      image.decode().catch(() => undefined).finally(() => {
        if (!cancelled) setDecoded((state) => state.map((value, item) => item === index ? true : value));
      });
    });
    return () => { cancelled = true; };
  }, [visible]);

  useEffect(() => {
    if (!ready || !visible) return;
    const openTimer = window.setTimeout(() => {
      setOpening(true);
      setIntro(false);
    }, 80);
    const settleTimer = window.setTimeout(() => setOpening(false), 840);
    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(settleTimer);
    };
  }, [ready, visible]);

  const panelState = (index: number) => {
    if (intro) return `${styles.cgWallPanel} ${styles[`cgWall${index + 1}`]}`;
    if (opening && index === 0) return `${styles.cgPanel} ${styles.cgFocusPanel} ${styles.cgOpeningFocus} ${styles[`cgWall${index + 1}`]}`;
    if (opening) return `${styles.cgPanel} ${styles[`cgStack${index + 1}`]} ${styles.cgOpeningStack} ${styles[`cgWall${index + 1}`]}`;
    if (index === active && phase === "release") return `${styles.cgPanel} ${styles.cgReleasePanel} ${styles[`cgStack${index + 1}`]}`;
    if (index === active && phase === "entry") return `${styles.cgPanel} ${styles.cgFocusPanel} ${styles.cgEnteringPanel} ${styles[`cgStack${index + 1}`]}`;
    if (index === active) return `${styles.cgPanel} ${styles.cgFocusPanel}`;
    if (index === target && phase === "release") return `${styles.cgPanel} ${styles[`cgWaiting${index + 1}`]}`;
    if (index < active || (active === 0 && index === 3)) return `${styles.cgPanel} ${styles[`cgStack${index + 1}`]}`;
    return `${styles.cgPanel} ${styles[`cgWaiting${index + 1}`]}`;
  };

  return (
    <section
      id="cg-walls"
      ref={chapterRef}
      className={styles.cgChapter}
      data-cg-tone="smoked"
      aria-label="CG Walls and Floors architectural material wall"
    >
      <div className={styles.cgHeader} data-cg-text>
        <p className={styles.cgKicker}>02 / Selected work · Interior renovation</p>
        <h2>CG Walls<br />&amp; Floors</h2>
        <p>Built work, made visible.</p>
      </div>

      <div className={styles.cgEditorial} data-cg-text aria-live="polite">
        <div><strong>{String(active + 1).padStart(2, "0")}</strong><span>/ 04</span></div>
        <p>{cgScreens[active].stage}</p>
        <h3>{cgScreens[active].headline}</h3>
        <small>{cgScreens[active].note}</small>
      </div>

      <div className={`${styles.cgScene} ${intro ? styles.cgSceneIntro : ""}`}>
        <div className={styles.cgLight} />
        <div className={styles.cgFloor} />
        {cgScreens.map((screen, index) => (
          <figure
            key={screen.src}
            className={panelState(index)}
            data-cg-panel={index + 1}
            style={{ "--cg-order": index } as CSSProperties}
          >
            <div className={styles.cgBoard}>
              <div className={styles.cgBoardFace}>
                <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="(max-width: 760px) 92vw, 70vw" alt={`CG Walls and Floors ${screen.label}`} width="2048" height={index === 0 ? "1136" : "1133"} decoding="async" loading="lazy" fetchPriority={index === 0 ? "high" : "auto"} />
              </div>
            </div>
            <figcaption>{String(index + 1).padStart(2, "0")} · {screen.label}</figcaption>
          </figure>
        ))}
        {intro && ready && (
          <div className={styles.cgIntroCopy}>
            <span>Architectural material wall</span>
            <strong>Four real surfaces.<br />One built standard.</strong>
          </div>
        )}
        {!ready && <div className={styles.cgLoading}>Preparing four real project captures</div>}
      </div>

      <nav className={styles.cgBreadcrumb} aria-label="CG Walls and Floors stages">
        {cgScreens.map((screen, index) => (
          <button
            key={screen.stage}
            type="button"
            className={index === active ? styles.cgCurrentCrumb : ""}
            aria-current={index === active ? "step" : undefined}
            aria-label={`Jump to ${screen.stage}, screen ${index + 1} of 4`}
            onClick={() => { setIntro(false); goTo(index); }}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>{screen.stage}
          </button>
        ))}
      </nav>
      <div className={styles.cgTiming}>4.50s hold <i /> 0.76s spatial transition <i /> Real capture · 2048px</div>
      <output id="cg-perf-metrics" hidden aria-hidden="true" />
    </section>
  );
}

const CADENCE_HOLD_MS = 4500;
const CADENCE_TRAVEL_MS = 900;
const CADENCE_ARRIVAL_MS = 300;
const CADENCE_CONNECTIONS = [22, 50, 78];

type CadencePhase = "idle" | "travel" | "pulse" | "entry";

function useCadenceSequence(enabled: boolean) {
  const [active, setActive] = useState(0);
  const [previous, setPrevious] = useState(0);
  const [target, setTarget] = useState(0);
  const [phase, setPhase] = useState<CadencePhase>("idle");
  const [manualUntil, setManualUntil] = useState(0);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
  }, []);

  const goTo = useCallback((index: number, manual = true) => {
    if (index === active && phase === "idle") {
      if (manual) setManualUntil(performance.now() + 9000);
      return;
    }
    clearTimers();
    setPrevious(active);
    setTarget(index);
    setPhase("travel");
    if (manual) setManualUntil(performance.now() + 9000);
    timers.current.push(window.setTimeout(() => {
      setPhase("pulse");
      timers.current.push(window.setTimeout(() => {
        setActive(index);
        setPhase("entry");
        timers.current.push(window.setTimeout(() => setPhase("idle"), 1050));
      }, CADENCE_ARRIVAL_MS));
    }, CADENCE_TRAVEL_MS));
  }, [active, clearTimers, phase]);

  useEffect(() => {
    if (!enabled || phase !== "idle") return;
    const wait = Math.max(CADENCE_HOLD_MS, manualUntil - performance.now());
    const timer = window.setTimeout(() => goTo((active + 1) % cadenceScreens.length, false), wait);
    return () => window.clearTimeout(timer);
  }, [active, enabled, goTo, manualUntil, phase]);

  useEffect(() => clearTimers, [clearTimers]);
  const pause = useCallback(() => {
    clearTimers();
    setPhase("idle");
    setManualUntil(Number.MAX_SAFE_INTEGER);
  }, [clearTimers]);
  return { active, previous, target, phase, goTo, pause };
}

type SequenceScrollDirection = "forward" | "backward";

function useSequenceScrollRelease(
  sectionRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  onRelease: (direction: SequenceScrollDirection) => void,
) {
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const release = (direction: SequenceScrollDirection) => {
      const section = sectionRef.current;
      if (!section) return;
      const bounds = section.getBoundingClientRect();
      // A sequence may only respond once its own viewport is fully in control.
      // The listeners are passive: the browser's native gesture is never blocked.
      if (bounds.top > 8 || bounds.bottom < window.innerHeight - 8) return;
      onRelease(direction);
      window.requestAnimationFrame(() => {
        const sectionTop = window.scrollY + section.getBoundingClientRect().top;
        const destination = direction === "forward"
          ? sectionTop + section.offsetHeight + 1
          : Math.max(0, sectionTop - window.innerHeight - 1);
        window.scrollTo({ top: destination, behavior: "auto" });
      });
    };
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY > 18) release("forward");
      else if (event.deltaY < -18) release("backward");
    };
    const onTouchStart = (event: TouchEvent) => {
      touchStartY.current = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const currentY = event.touches[0]?.clientY;
      if (touchStartY.current === null || currentY === undefined) return;
      const delta = touchStartY.current - currentY;
      if (delta > 36) release("forward");
      else if (delta < -36) release("backward");
    };
    const onTouchEnd = () => { touchStartY.current = null; };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, onRelease, sectionRef]);
}

function useCadencePerformanceAudit(active: number, decoded: boolean[], enabled: boolean) {
  const stateRef = useRef({ active, decoded, enabled });
  useEffect(() => { stateRef.current = { active, decoded, enabled }; }, [active, decoded, enabled]);

  useEffect(() => {
    if (!performanceAuditEnabled()) return;
    const intervals: number[] = [];
    const longTasks: number[] = [];
    const misses = new Set<number>();
    let cls = 0;
    let previous = performance.now();
    let frame = 0;
    let samples = 0;
    let collisions = 0;
    let minGap = Number.POSITIVE_INFINITY;

    const sample = () => {
      const rects = [...document.querySelectorAll<HTMLElement>("[data-cadence-screen]")]
        .map((node) => node.getBoundingClientRect())
        .filter((rect) => rect.width > 2 && rect.height > 2);
      for (let a = 0; a < rects.length; a += 1) {
        for (let b = a + 1; b < rects.length; b += 1) {
          const dx = Math.max(rects[a].left - rects[b].right, rects[b].left - rects[a].right, 0);
          const dy = Math.max(rects[a].top - rects[b].bottom, rects[b].top - rects[a].bottom, 0);
          const gap = Math.hypot(dx, dy);
          minGap = Math.min(minGap, gap);
          if (gap < (window.innerWidth <= 720 ? 20 : 48)) collisions += 1;
        }
      }
      samples += 1;
    };

    const update = () => {
      const interactive = intervals.filter((value) => value < 250);
      const sorted = [...interactive].sort((a, b) => a - b);
      const elapsed = interactive.reduce((sum, value) => sum + value, 0);
      const rawElapsed = intervals.reduce((sum, value) => sum + value, 0);
      window.__AVERR_CADENCE_PERF__ = {
        fps: elapsed ? interactive.length * 1000 / elapsed : 0,
        rawFps: rawElapsed ? intervals.length * 1000 / rawElapsed : 0,
        p95FrameInterval: sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * .95))] || 0,
        maxFrameInterval: sorted.at(-1) || 0,
        suspensionGaps: intervals.length - interactive.length,
        longTasksOver50ms: longTasks.length,
        longestTask: Math.max(0, ...longTasks),
        cls,
        decodedBeforeFocus: misses.size === 0,
        decodeMisses: [...misses],
        touchAction: getComputedStyle(document.querySelector("main") || document.body).touchAction,
        scrollListenerPassive: true,
        minSurfaceGap: Number.isFinite(minGap) ? minGap : null,
        collisions,
        samples,
      };
      const output = document.getElementById("cadence-perf-metrics");
      if (output) output.dataset.snapshot = JSON.stringify(window.__AVERR_CADENCE_PERF__);
    };

    const tick = (now: number) => {
      if (stateRef.current.enabled) {
        intervals.push(now - previous);
        if (!stateRef.current.decoded[stateRef.current.active]) misses.add(stateRef.current.active + 1);
        if (frame % 6 === 0) {
          sample();
          update();
        }
      }
      previous = now;
      frame += 1;
      raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    const observers: PerformanceObserver[] = [];
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
        if (stateRef.current.enabled && entry.duration > 50) longTasks.push(entry.duration);
      }));
      observer.observe({ type: "longtask" });
      observers.push(observer);
    } catch { /* Optional API. */ }
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
        const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!shift.hadRecentInput) cls += shift.value || 0;
      }));
      observer.observe({ type: "layout-shift", buffered: true });
      observers.push(observer);
    } catch { /* Optional API. */ }
    return () => {
      cancelAnimationFrame(raf);
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);
}

function CadenceChapter() {
  const chapterRef = useRef<HTMLElement>(null);
  const [decoded, setDecoded] = useState(() => cadenceScreens.map(() => false));
  const [visible, setVisible] = useState(false);
  const [intro, setIntro] = useState(true);
  const [opening, setOpening] = useState(false);
  // Match the other work chapters: entry is driven by scrolling into view,
  // while later captures finish decoding ahead of their turn.
  const ready = decoded[0];
  const { active, previous, target, phase, goTo, pause } = useCadenceSequence(ready && visible && !intro && !opening);
  useCadencePerformanceAudit(active, decoded, ready && visible && !intro);

  const releaseCadence = useCallback(() => {
    pause();
    setIntro(false);
    setOpening(false);
  }, [pause]);

  useSequenceScrollRelease(chapterRef, visible, releaseCadence);

  useEffect(() => {
    const chapter = chapterRef.current;
    if (!chapter) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= .18), { threshold: [.18] });
    observer.observe(chapter);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    cadenceScreens.forEach((screen, index) => {
      warmChapterImage(screen.src).finally(() => {
        if (!cancelled) setDecoded((state) => state.map((value, item) => item === index ? true : value));
      });
    });
    return () => { cancelled = true; };
  }, [visible]);

  useEffect(() => {
    if (!ready || !visible || !intro) return;
    const openTimer = window.setTimeout(() => {
      setIntro(false);
      setOpening(true);
    }, 80);
    return () => window.clearTimeout(openTimer);
  }, [intro, ready, visible]);

  useEffect(() => {
    if (intro || !opening) return;
    const settleTimer = window.setTimeout(() => setOpening(false), 1100);
    return () => window.clearTimeout(settleTimer);
  }, [intro, opening]);

  const sideFor = (index: number, center: number) => ((index - center + cadenceScreens.length) % cadenceScreens.length === 1 ? "right" : "left");
  const screenClass = (index: number) => {
    if (intro) return `${styles.cadenceScreen} ${index === 0 ? styles.cadenceParkLeft : index === 1 ? styles.cadenceParkCenter : styles.cadenceParkRight}`;
    if (opening && index === 0) return `${styles.cadenceScreen} ${styles.cadenceFocused} ${styles.cadenceOpening}`;
    if (opening) return `${styles.cadenceScreen} ${sideFor(index, 0) === "right" ? styles.cadenceRecededRight : styles.cadenceRecededLeft}`;
    if (phase === "travel" && index === active) return `${styles.cadenceScreen} ${sideFor(index, target) === "right" ? styles.cadenceExitRight : styles.cadenceExitLeft}`;
    if (phase === "travel" && index === target) return `${styles.cadenceScreen} ${sideFor(index, active) === "right" ? styles.cadenceRecededRight : styles.cadenceRecededLeft}`;
    if (phase === "travel") {
      const begins = sideFor(index, active);
      return `${styles.cadenceScreen} ${begins === "right" ? styles.cadenceTransitRightToLeft : styles.cadenceTransitLeftToRight}`;
    }
    if (phase === "pulse" && index === active) return `${styles.cadenceScreen} ${sideFor(index, target) === "right" ? styles.cadenceLaneLowerRight : styles.cadenceLaneLowerLeft}`;
    if (phase === "pulse" && index !== target) return `${styles.cadenceScreen} ${sideFor(index, target) === "right" ? styles.cadenceLaneUpperRight : styles.cadenceLaneUpperLeft}`;
    if (phase === "entry" && index === active) {
      return `${styles.cadenceScreen} ${styles.cadenceFocused} ${sideFor(index, previous) === "right" ? styles.cadenceEnterRight : styles.cadenceEnterLeft}`;
    }
    if (phase === "entry" && index === previous) return `${styles.cadenceScreen} ${sideFor(index, active) === "right" ? styles.cadenceSettleLowerRight : styles.cadenceSettleLowerLeft}`;
    if (phase === "entry") return `${styles.cadenceScreen} ${sideFor(index, active) === "right" ? styles.cadenceSettleUpperRight : styles.cadenceSettleUpperLeft}`;
    if (index === active) return `${styles.cadenceScreen} ${styles.cadenceFocused}`;
    return `${styles.cadenceScreen} ${sideFor(index, active) === "right" ? styles.cadenceRecededRight : styles.cadenceRecededLeft}`;
  };

  const signalFrom = CADENCE_CONNECTIONS[active];
  const signalTo = CADENCE_CONNECTIONS[target];
  const signalLeft = Math.min(signalFrom, signalTo);
  const signalWidth = Math.abs(signalTo - signalFrom);

  return (
    <section
      id="cadencestack"
      ref={chapterRef}
      className={styles.cadenceChapter}
      style={{
        "--signal-left": `${signalLeft}%`,
        "--signal-width": `${signalWidth}%`,
        "--signal-origin": signalTo >= signalFrom ? "left" : "right",
        "--active-connection": `${CADENCE_CONNECTIONS[active]}%`,
        "--target-connection": `${CADENCE_CONNECTIONS[target]}%`,
      } as CSSProperties}
      aria-label="CadenceStack signal analysis bench"
      data-scroll-skip="enabled"
    >
      <header className={styles.cadenceHeader}>
        <p>03 / Selected work · LinkedIn intelligence</p>
        <h2>CadenceStack</h2>
        <span>Signal analysis bench</span>
      </header>

      <aside key={active} className={`${styles.cadenceEditorial} ${(opening || phase === "entry") ? styles.cadenceEditorialReveal : ""}`} aria-live="polite">
        <p>{String(active + 1).padStart(2, "0")} · {cadenceScreens[active].stage}</p>
        <h3>{cadenceScreens[active].headline}</h3>
        <span>{cadenceScreens[active].note}</span>
      </aside>

      <div className={styles.cadenceBench}>
        <div className={styles.cadenceBenchLight} />
        <div className={styles.cadenceBenchEdge} />
        <div className={styles.cadenceConduit} aria-hidden="true">
          <i className={styles.cadenceGroove} />
          {CADENCE_CONNECTIONS.map((left, index) => <i key={left} className={styles.cadenceBranch} style={{ left: `${left}%` }} data-connection={index + 1} />)}
          <i className={`${styles.cadenceSignal} ${phase === "travel" ? styles.cadenceSignalTravel : ""}`} />
          <i className={`${styles.cadenceArrival} ${phase === "pulse" ? styles.cadenceArrivalPulse : ""}`} />
          <i className={styles.cadenceIdleFeed} />
        </div>

        {cadenceScreens.map((screen, index) => (
          <figure
            key={screen.src}
            className={screenClass(index)}
            data-cadence-screen={index + 1}
            onClick={() => index !== active && goTo(index)}
          >
            <div className={styles.cadenceInstrumentFrame}>
              <div className={styles.cadenceScreenFace}>
                <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="(max-width: 760px) 92vw, 70vw" alt={`CadenceStack ${screen.label}`} width="2048" height={index === 0 ? "1080" : index === 1 ? "1131" : "1081"} decoding="async" loading="lazy" fetchPriority={index === 0 ? "high" : "auto"} />
              </div>
              <i className={styles.cadenceDock} />
            </div>
            <figcaption>{String(index + 1).padStart(2, "0")} · {screen.label}</figcaption>
          </figure>
        ))}

        {intro && ready && (
          <div className={styles.cadenceIntro}>
            <span>CadenceStack · Signal analysis bench</span>
            <strong>One signal.<br />Three surfaces it moves through.</strong>
          </div>
        )}
        {!ready && <div className={styles.cadenceLoading}>Calibrating three real product captures</div>}
      </div>

      <nav className={styles.cadenceBreadcrumb} aria-label="CadenceStack signal stages">
        {cadenceScreens.map((screen, index) => (
          <button key={screen.stage} type="button" className={index === active ? styles.cadenceCurrentCrumb : ""} aria-current={index === active ? "step" : undefined} onClick={() => { setIntro(false); setOpening(false); goTo(index); }}>
            <span>{String(index + 1).padStart(2, "0")}</span>{screen.stage}
          </button>
        ))}
      </nav>
      <div className={styles.cadenceTiming}>4.50s hold <i /> 0.90s signal travel <i /> 0.30s arrival pulse</div>
      <output id="cadence-perf-metrics" hidden aria-hidden="true" />
    </section>
  );
}

function CapitalCommandChapter() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(0);
  const [opening, setOpening] = useState(true);
  const [finalReveal, setFinalReveal] = useState(false);
  const [inView, setInView] = useState(false);
  const manualPause = useRef(false);

  const goTo = useCallback((index: number, byPerson = false) => {
    if (byPerson) manualPause.current = true;
    setOpening(false);
    setFinalReveal(index === capitalScreens.length);
    if (index < capitalScreens.length) setActive(index);
  }, []);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || manualPause.current) return;
    const hold = finalReveal ? 6000 : opening ? 1800 : 4500;
    const timer = window.setTimeout(() => {
      if (opening) {
        setOpening(false);
        setActive(0);
      } else if (finalReveal) {
        setFinalReveal(false);
        setActive(0);
      } else if (active === capitalScreens.length - 1) {
        setFinalReveal(true);
      } else {
        setActive((value) => value + 1);
      }
    }, hold);
    return () => window.clearTimeout(timer);
  }, [active, finalReveal, inView, opening]);

  useEffect(() => {
    if (!performanceAuditEnabled()) return;
    if (!inView) return;
    const intervals: number[] = [];
    const longTasks: number[] = [];
    let cls = 0;
    let frame = 0;
    let last = performance.now();
    const started = last;
    const observers: PerformanceObserver[] = [];
    try {
      const longTaskObserver = new PerformanceObserver((list) => list.getEntries().forEach((entry) => longTasks.push(entry.duration)));
      longTaskObserver.observe({ type: "longtask", buffered: true });
      observers.push(longTaskObserver);
    } catch {}
    try {
      const layoutObserver = new PerformanceObserver((list) => list.getEntries().forEach((entry) => {
        const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
        if (!shift.hadRecentInput) cls += shift.value ?? 0;
      }));
      layoutObserver.observe({ type: "layout-shift", buffered: true });
      observers.push(layoutObserver);
    } catch {}
    const tick = (now: number) => {
      const delta = now - last;
      if (delta > 0 && delta < 250) intervals.push(delta);
      last = now;
      if (now - started < 8000) frame = requestAnimationFrame(tick);
      else {
        const ordered = [...intervals].sort((a, b) => a - b);
        const p95 = ordered[Math.min(ordered.length - 1, Math.floor(ordered.length * .95))] ?? 0;
        window.__AVERR_CAPITAL_PERF__ = {
          fps: intervals.length ? 1000 / (intervals.reduce((sum, value) => sum + value, 0) / intervals.length) : 0,
          p95FrameInterval: p95,
          longTasksOver50ms: longTasks.filter((duration) => duration > 50).length,
          longestTask: Math.max(0, ...longTasks),
          cls,
          samples: intervals.length,
        };
        observers.forEach((observer) => observer.disconnect());
      }
    };
    frame = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(frame); observers.forEach((observer) => observer.disconnect()); };
  }, [inView]);

  const current = capitalScreens[active];

  return (
    <section ref={sectionRef} id="capital-command" className={styles.capitalSequence} aria-label="Capital Command command theatre">
      <div className={styles.capitalPinned}>
        <header className={styles.capitalHeader} data-capital-title>
          <div>
            <p>04 / Selected work · Command theatre</p>
            <h2>Capital Command</h2>
            <span>Simulated research environment. No live trades are placed.</span>
          </div>
          <aside aria-live="polite">
            <p>{finalReveal ? "Console complete" : opening ? "Opening sequence" : `${String(active + 1).padStart(2, "0")} / 07 · ${current.label}`}</p>
            <h3>{finalReveal ? "Seven instruments. One console." : opening ? "Seven instruments. One console." : current.headline}</h3>
            <span>{finalReveal ? "Every research surface returns to its place, visible and ready as one connected operating view." : opening ? "A complete cross-market research system, surfaced one instrument at a time." : current.note}</span>
          </aside>
        </header>

        <div className={`${styles.capitalTheatre} ${opening ? styles.capitalOpening : ""} ${finalReveal ? styles.capitalFinal : ""}`} data-capital-theatre>
          <div className={styles.capitalRoomLight} aria-hidden="true" />
          <div className={styles.capitalConsoleSurface} aria-hidden="true"><i /><i /></div>
          <div className={styles.capitalArm} aria-hidden="true"><i /><b /></div>

          {capitalScreens.map((screen, index) => {
            const isActive = !opening && !finalReveal && active === index;
            const className = `${styles.capitalConsoleScreen} ${isActive ? styles.capitalConsoleScreenActive : ""}`;
            return (
              <figure
                key={screen.label}
                className={className}
                style={{ "--capital-index": index } as CSSProperties}
                data-capital-screen={index + 1}
                data-active={isActive ? "true" : "false"}
                onClick={() => goTo(index, true)}
              >
                <div className={styles.capitalMonitor}>
                  <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="(max-width: 760px) 92vw, 70vw" alt={`Capital Command ${screen.label}`} width="2048" height="1133" decoding="async" loading="lazy" />
                </div>
                <figcaption>{String(index + 1).padStart(2, "0")} · {screen.label}</figcaption>
              </figure>
            );
          })}

          {opening && <div className={styles.capitalOpeningCard}><span>Capital Command</span><strong>Seven instruments.<br />One console.</strong></div>}
          {finalReveal && <div className={styles.capitalFinalLabel}><span>Full system view</span><strong>07 / 07 instruments online</strong></div>}
        </div>

        <nav className={styles.capitalStageNav} aria-label="Capital Command stages">
          {capitalScreens.map((screen, index) => (
            <button key={screen.label} type="button" aria-current={!finalReveal && !opening && index === active ? "step" : undefined} onClick={() => goTo(index, true)}>
              <span>{String(index + 1).padStart(2, "0")}</span>{screen.label}
            </button>
          ))}
          <button type="button" aria-current={finalReveal ? "step" : undefined} onClick={() => goTo(capitalScreens.length, true)}><span>08</span>Full console</button>
        </nav>
        <div className={styles.capitalTiming}>4.50s hold <i /> 0.80s mechanical lift / return <i /> 6.00s full-console reveal</div>
      </div>
    </section>
  );
}

export default function WorkPage({ embedded = false }: { embedded?: boolean }) {
  const [decoded, setDecoded] = useState(() => screens.map(() => false));
  const [nearby, setNearby] = useState(!embedded);
  const presented = decoded.every(Boolean);
  const [openingComplete, setOpeningComplete] = useState(false);
  const { sectionRef, progress, jumpToStage } = useCorridorProgress(presented && openingComplete);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const rawStage = progress * (screens.length - 1);
  const segment = Math.min(screens.length - 1, Math.floor(rawStage));
  const local = segment === screens.length - 1 ? 0 : rawStage - segment;
  const activeIndex = Math.min(screens.length - 1, segment + (local >= .94 ? 1 : 0));
  const nextIndex = (activeIndex + 1) % screens.length;
  const poses = useMemo(() => screens.map((_, index) => getPose(index, segment, local)), [segment, local]);
  usePerformanceAudit(activeIndex, decoded, presented);

  useEffect(() => {
    if (!embedded || nearby) return;
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setNearby(true);
        observer.disconnect();
      }
    }, { rootMargin: "1200px 0px", threshold: 0 });
    observer.observe(section);
    return () => observer.disconnect();
  }, [embedded, nearby, sectionRef]);

  useEffect(() => {
    if (embedded) return;
    // `/work` is a separate route, so native scrolling cannot cross back into
    // `/`. A continued upward gesture at the true document boundary provides
    // the missing reverse path without intercepting normal page scrolling.
    let upwardTravel = 0;
    let lastWheel = 0;
    let touchStartY: number | null = null;
    let leaving = false;
    const returnToLanding = () => {
      if (leaving) return;
      leaving = true;
      window.location.assign("/");
    };
    const onWheel = (event: WheelEvent) => {
      if (window.scrollY > 1 || event.deltaY >= 0) {
        upwardTravel = 0;
        return;
      }
      const now = performance.now();
      if (now - lastWheel > 420) upwardTravel = 0;
      lastWheel = now;
      upwardTravel += Math.abs(event.deltaY);
      if (upwardTravel >= 90) returnToLanding();
    };
    const onTouchStart = (event: TouchEvent) => {
      touchStartY = window.scrollY <= 1 ? event.touches[0]?.clientY ?? null : null;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (touchStartY === null || window.scrollY > 1) return;
      const currentY = event.touches[0]?.clientY;
      if (currentY !== undefined && currentY - touchStartY >= 80) returnToLanding();
    };
    const onTouchEnd = () => { touchStartY = null; };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [embedded]);

  useEffect(() => {
    if (!nearby) return;
    let cancelled = false;
    screens.forEach((screen, index) => {
      const image = new Image();
      image.src = screen.src;
      image.decode().catch(() => undefined).finally(() => {
        if (!cancelled) setDecoded((state) => state.map((value, item) => item === index ? true : value));
      });
    });
    return () => { cancelled = true; };
  }, [nearby]);

  useEffect(() => {
    if (!presented) return;
    const timer = window.setTimeout(() => setOpeningComplete(true), 2400);
    return () => window.clearTimeout(timer);
  }, [presented]);

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setPointer({
      x: ((event.clientX - bounds.left) / bounds.width - .5) * 2,
      y: ((event.clientY - bounds.top) / bounds.height - .5) * 2,
    });
  };

  return (
    <main className={styles.page} data-work-scroll-release="native">
      <header className={`${styles.header} ${embedded ? styles.embeddedHeader : ""}`}>
        <Link className={styles.wordmark} href="/" aria-label="Averr Studios home"><img src="/brand/averr-studios-reverse.svg" alt="Averr Studios" width="1800" height="420" /></Link>
        <nav aria-label="Primary navigation">
          <a className={styles.activeNav} href="#work-top">Work</a>
          <a href="#services">Services</a>
          <a href="#founder">Founder</a>
          <a className={styles.headerCta} href="#contact">Start a conversation</a>
        </nav>
      </header>

      <section id="work-top" ref={sectionRef} className={styles.sequence} aria-label="SIFT precision corridor">
        <div className={styles.sticky}>
          <div className={styles.chapterCopy}>
            <div>
              <p className={styles.kicker}>01 / Selected work · AI job-search system</p>
              <h1>SIFT</h1>
              <p className={styles.promise}>Precision over volume.</p>
              <div className={styles.proofMeta}>
                <span>{screens.length} proofs</span>
                <span>Auto · Scroll to override</span>
              </div>
            </div>
          </div>

          <aside className={styles.editorial} aria-live="polite">
            <div className={styles.stageIndex}><strong>{String(activeIndex + 1).padStart(2, "0")}</strong><span>/ {String(screens.length).padStart(2, "0")}</span></div>
            <p className={styles.stageName}>{screens[activeIndex].stage}</p>
            <h2>{screens[activeIndex].headline}</h2>
            <p className={styles.stageNote}>{screens[activeIndex].note}</p>
          </aside>

          <div
            className={styles.corridor}
            onPointerMove={onPointerMove}
            onPointerLeave={() => setPointer({ x: 0, y: 0 })}
            style={{ "--pointer-x": pointer.x, "--pointer-y": pointer.y, "--primary-width": `${PRIMARY_WIDTH}vw` } as CSSProperties}
          >
            <div className={styles.lightField} />
            <div className={styles.ceilingPlane} />
            <div className={styles.sideWallLeft} />
            <div className={styles.sideWallRight} />
            <div className={styles.corridorFloor} />
            <div className={styles.frames}>
              {screens.map((screen, index) => {
                const pose = poses[index];
                return (
                  <figure
                    key={screen.src}
                    className={styles.surface}
                    data-sift-frame={index + 1}
                    data-prominence={pose.prominence.toFixed(3)}
                    style={{
                      "--x": `${pose.x}vw`, "--y": `${pose.y}vh`, "--z": `${pose.z}px`,
                      "--mobile-x": `${pose.x / SIDE_X * 84}vw`,
                      "--scale": pose.scale, "--rotate-y": `${pose.rotateY}deg`,
                      "--surface-opacity": pose.opacity, "--prominence": pose.prominence,
                      "--surface-index": index,
                      zIndex: 10 + Math.round(pose.prominence * 20),
                    } as CSSProperties}
                  >
                    <div className={styles.materialFrame}>
                      <div className={styles.screenGlass}>
                        <img
                          src={screen.src}
                          srcSet={workImageSrcSet(screen.src)}
                          sizes="(max-width: 760px) 92vw, 70vw"
                          alt={`SIFT ${screen.label} product screen`}
                          width={screen.width}
                          height={screen.height}
                          decoding="async"
                          loading="lazy"
                          fetchPriority="auto"
                        />
                      </div>
                    </div>
                    <figcaption>{String(index + 1).padStart(2, "0")} · {screen.label}</figcaption>
                  </figure>
                );
              })}
            </div>

            <aside className={styles.nextPreview} aria-label={`Next up: ${screens[nextIndex].label}`}>
              <div><span>Next up</span><strong>{screens[nextIndex].label}</strong></div>
              <img src={siftPreviewSrc(screens[nextIndex].src)} alt="" width="180" height="99" loading="lazy" decoding="async" />
            </aside>
          </div>

          <nav className={styles.breadcrumb} aria-label="SIFT journey stages">
            {screens.map((screen, index) => (
              <button
                key={screen.stage}
                type="button"
                className={index === activeIndex ? styles.currentCrumb : ""}
                aria-label={`Jump to ${screen.stage}, screen ${index + 1} of ${screens.length}`}
                aria-current={index === activeIndex ? "step" : undefined}
                data-stage-target={index + 1}
                onClick={() => jumpToStage(index)}
              >
                {screen.stage}
              </button>
            ))}
          </nav>
          <div className={styles.counter} aria-label={`Screen ${activeIndex + 1} of ${screens.length}`}>
            <span>Real product capture</span><i /><span>2048px</span>
          </div>
          <div className={`${styles.openingCurtain} ${presented && !openingComplete ? styles.openingCurtainVisible : ""}`} aria-hidden={openingComplete}>
            <span>SIFT · Precision corridor</span>
            <strong>Nine real surfaces.<br />One controlled journey.</strong>
            <i />
          </div>
          {!presented && <div className={styles.decodeCover} aria-label="Preparing SIFT evidence"><span>Preparing evidence</span></div>}
          <div className={styles.progress}><span style={{ transform: `scaleX(${progress})` }} /></div>
          <output id="sift-perf-metrics" hidden aria-hidden="true" />
        </div>
      </section>

      <section className={styles.chapterRelease}>
        <div className={styles.releaseIndex}><span>{String(screens.length).padStart(2, "0")} / {String(screens.length).padStart(2, "0")}</span><i /></div>
        <div>
          <p className={styles.kicker}>SIFT chapter complete · {screens.length} proofs reviewed</p>
          <h2>Every surface.<br />Every decision visible.</h2>
          <p>The full product journey has been presented before the next project chapter begins.</p>
        </div>
      </section>

      <CgChapter />

      <CadenceChapter />

      <CapitalCommandChapter />

      <PostWork />

      <section className={styles.reducedSequence} aria-label="SIFT screenshots">
        {screens.map((screen, index) => (
          <article key={screen.src}>
            <div><span>{String(index + 1).padStart(2, "0")} / {String(screens.length).padStart(2, "0")}</span><h2>{screen.label}</h2><p>{screen.note}</p></div>
            <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="92vw" alt={`SIFT ${screen.label} product screen`} width={screen.width} height={screen.height} loading="lazy" decoding="async" />
          </article>
        ))}
      </section>
      <section className={styles.reducedCgSequence} aria-label="CG Walls and Floors screenshots">
        <header><span>02 / Selected work</span><h2>CG Walls &amp; Floors</h2><p>Built work, made visible.</p></header>
        {cgScreens.map((screen, index) => (
          <article key={screen.src}>
            <div><span>{String(index + 1).padStart(2, "0")} / 04 · {screen.stage}</span><h3>{screen.headline}</h3><p>{screen.note}</p></div>
            <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="92vw" alt={`CG Walls and Floors ${screen.label}`} width="2048" height={index === 0 ? "1136" : "1133"} loading="lazy" decoding="async" />
          </article>
        ))}
      </section>
      <section className={styles.reducedCadenceSequence} aria-label="CadenceStack screenshots">
        <header><span>03 / Selected work</span><h2>CadenceStack</h2><p>One signal. Three surfaces it moves through.</p></header>
        {cadenceScreens.map((screen, index) => (
          <article key={screen.src}>
            <div><span>{String(index + 1).padStart(2, "0")} / 03 · {screen.stage}</span><h3>{screen.headline}</h3><p>{screen.note}</p></div>
            <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="92vw" alt={`CadenceStack ${screen.label}`} width="2048" height={index === 0 ? "1080" : index === 1 ? "1131" : "1081"} loading="lazy" decoding="async" />
          </article>
        ))}
      </section>
      <section className={styles.reducedCapitalSequence} aria-label="Capital Command screenshots">
        <header><span>04 / Selected work</span><h2>Capital Command</h2><p>Simulated research environment. No live trades are placed.</p></header>
        {capitalScreens.map((screen, index) => (
          <article key={screen.label}>
            <div><span>{String(index + 1).padStart(2, "0")} / 07 · {screen.label}</span><h3>{screen.headline}</h3><p>{screen.note}</p></div>
            <img src={screen.src} srcSet={workImageSrcSet(screen.src)} sizes="92vw" alt={`Capital Command ${screen.label}`} width="2048" height="1133" loading="lazy" decoding="async" />
          </article>
        ))}
      </section>
    </main>
  );
}
